import React, { useRef, useState } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Loading from '~/components/atom/Loading';
import { Body, Heading4, Label, Variant } from '~/components/atom/Typography';
import ErrorScreen from '~/components/page/ErrorScreen';
import { useGetLfaConversionQuery } from '~/graphql/types';
import Divider from '~/components/atom/Divider';
import LeadInfoBlock from './components/LeadInfoBlock';
import ActionBar from './components/ActionBar';
import FeedbackModal from '../FeedbackModal';
import getDateAndTimeString from '../../utils/getDateAndTimeString';
import Link from '~/components/molecule/Link';
import styled, { css } from 'styled-components';
import SpringContainer from '~/components/molecule/SpringContainer';
import TimelineSection from './components/TimelineSection';
import CopyButton from '~/components/molecule/CopyButton';
import { isProduction } from '~/util';
import CopyTextBlock from '~/components/molecule/CopyTextBlock';
import MeetingLocation from './components/MeetingLocation';

const text = {
  viewContactLink: 'Bekijk contact',
  updateBefore: 'Updaten vóór:',
  updateStatusButtonLabel: 'Status van de afspraak updaten',
  copyLinkLabel: 'Kopieer link',
};

type Props = {
  conversionId: string;
  accountId: string;
};

const LeadOverview: React.FCC<Props> = ({ conversionId, accountId }) => {
  const isDev = isProduction() !== true;

  const { data, loading, error, refetch } = useGetLfaConversionQuery({
    variables: { accountId, lfaConversionId: conversionId },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  if (loading)
    return (
      <JustificationContainer justification="center" width="100%">
        <Loading />
      </JustificationContainer>
    );

  if (!data?.getLfaConversion || error) return <ErrorScreen />;

  const {
    name,
    address,
    email,
    phone,
    officeId,
    userId,
    selectedServices,
    contactId,
    appointmentDate,
    autoAcceptDate,
    createdDaysAgo,
    Activities,
    id,
    _v,
    lastServiceFeedback,
    isFinalised,
    lfaRequestId,
    location: meetingLocation,
  } = data.getLfaConversion;

  const { date } = getDateAndTimeString(autoAcceptDate);

  const shareLink = location.origin + `/-/lfa/?query=${id}&tab=all`;

  return (
    <>
      {modalOpen && (
        <FeedbackModal
          onClose={() => setModalOpen(false)}
          selectedServices={selectedServices}
          conversionId={id}
          conversionVersion={_v}
          lastServiceFeedback={lastServiceFeedback}
          feedbackFor={name}
        />
      )}
      <Container>
        <InnerContainer
          ref={containerRef}
          padding={['l', 'xl', 'xl', 'xl']}
          backgroundColor={{ group: 'white' }}
          border={{
            radius: 'm',
            width: 's',
            color: { group: 'primary', variant: 'translucent' },
          }}
          width="100%"
          direction="column"
          height="1000px"
        >
          <JustificationContainer
            align="center"
            width="100%"
            margin={[null, null, 'xxxs', null]}
            padding={['xs', null]}
            gap="xs"
            justification="space-between"
          >
            <JustificationContainer align="center" gap="xs">
              <Heading4 margin={[null]} variant={Variant.primary}>
                {name}
              </Heading4>
              <StyledLink target="_blank" to={`/contacts/${contactId}`}>
                {text.viewContactLink}
              </StyledLink>
            </JustificationContainer>
            <CopyButton
              textToShare={shareLink}
              label={text.copyLinkLabel}
              icon="link"
            />
          </JustificationContainer>
          {isDev && (
            <CopyTextBlock
              textToCopy={lfaRequestId}
              label="LFA Request ID"
              margin={[null, null, 'xxs', null]}
            />
          )}

          {meetingLocation ? (
            <MeetingLocation location={meetingLocation} />
          ) : (
            <Divider margin={['xxxs', null, null, null]} />
          )}

          <LeadInfoBlock
            refetch={refetch}
            contactId={contactId}
            name={name}
            email={email}
            phone={phone}
            officeId={officeId}
            userId={userId}
            address={address}
            selectedServices={selectedServices}
            appointmentDate={appointmentDate}
            createdDaysAgo={createdDaysAgo}
          />
          <Divider margin={[null, null, 'xxs', null]} />
          <TimelineSection
            contact={{ id: contactId, name, email }}
            activities={Activities.items}
          />
        </InnerContainer>
      </Container>
      {!isFinalised && (
        <ActionBar
          ref={containerRef}
          information={
            <JustificationContainer align="center" gap="xxs">
              <Label size="s" margin={[null]} color={{ group: 'primary' }}>
                {text.updateBefore}
              </Label>
              <Body size="s" margin={[null]}>
                {date}
              </Body>
            </JustificationContainer>
          }
          action={{
            onClick: () => setModalOpen(true),
            label: text.updateStatusButtonLabel,
          }}
        />
      )}
    </>
  );
};

const StyledLink = styled(Link)(
  ({ theme }) => css`
    margin-top: ${theme.space('xxxs')};
  `,
);

const InnerContainer = styled(JustificationContainer)`
  overflow-x: hidden;
`;

const Container = styled(SpringContainer)`
  flex-grow: 1;
`;

export default LeadOverview;
